import queryString from 'query-string';
import { fromSeoUrl, toPlural, toSeoUrl } from 'helpers/HelperFunctions';
import { MAX_URL_PAGE } from './jobsPagination';

/** Query keys that make a jobs URL a filtered variant. `page` is not one. */
const FILTER_QUERY_KEYS = [
  'roles',
  'experienceLevel',
  'hiringCapacity',
  'remote',
  'status',
  'internal',
  'searchTerm',
];

function parseQuery(query) {
  if (!query) {
    return {};
  }
  if (typeof query === 'string') {
    const search = query.startsWith('?') ? query : `?${query}`;
    return queryString.parse(search);
  }
  return { ...query };
}

function hasParamValue(value) {
  if (value === undefined || value === null || value === '') {
    return false;
  }
  if (Array.isArray(value)) {
    return value.some(
      (item) => item !== undefined && item !== null && item !== ''
    );
  }
  return true;
}

function hasFilterQuery(params) {
  return FILTER_QUERY_KEYS.some((key) => hasParamValue(params[key]));
}

/**
 * Normalize a role name or slug to the /jobs/:role path segment.
 * @param {string|null|undefined} role
 * @returns {string|null}
 */
export function normalizeJobsRoleSlug(role) {
  if (!role) {
    return null;
  }
  return toSeoUrl(toPlural(fromSeoUrl(String(role))));
}

function normalizeCountrySlug(country) {
  if (!country) {
    return null;
  }
  return toSeoUrl(String(country));
}

/**
 * When ?roles= names exactly one role, return its path slug; otherwise null.
 * @param {object} params
 * @returns {string|null}
 */
function singleRoleSlugFromQuery(params) {
  const { roles } = params;
  if (!hasParamValue(roles)) {
    return null;
  }

  let candidates;
  if (Array.isArray(roles)) {
    candidates = roles.filter(
      (item) => item !== undefined && item !== null && item !== ''
    );
  } else if (String(roles).includes(',')) {
    candidates = String(roles)
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);
  } else {
    candidates = [roles];
  }

  if (candidates.length !== 1) {
    return null;
  }

  return normalizeJobsRoleSlug(candidates[0]);
}

function buildCleanJobsPath({ role, country, city, categories }) {
  if (categories && !role && !country) {
    return '/jobs/categories';
  }

  let path = '/jobs';
  if (role) {
    path += `/${role}`;
  }
  if (country) {
    path += `/in/${country}`;
    if (city) {
      path += `/${city}`;
    }
  }
  return path;
}

/**
 * Resolve the jobs-board canonical URL.
 *
 * Clean paths (/jobs, role, country, city, role+country, categories) self-canonicalize.
 * Clean path + only ?page=N (N>1) keeps page in the canonical so crawlers can walk batches.
 * Filter query params canonicalize to the nearest clean path (city kept when present;
 * a single ?roles= is promoted when the path has no role). Page is omitted on filtered URLs.
 *
 * Indexing is left to the canonical alone — no robots noindex is emitted.
 *
 * @param {object} options
 * @param {string} [options.role] - path :role slug or display name
 * @param {string} [options.country] - path :country slug or name
 * @param {string} [options.city] - path :city slug or name
 * @param {boolean} [options.categories] - true for /jobs/categories
 * @param {object|string} [options.query] - query object or location.search
 * @param {string} [options.baseUrl] - optional origin, no trailing slash required
 * @returns {{ canonical: string }}
 */
export default function getJobsCanonical({
  role = null,
  country = null,
  city = null,
  categories = false,
  query = {},
  baseUrl = '',
} = {}) {
  const params = parseQuery(query);
  const roleSlug = normalizeJobsRoleSlug(role);
  const countrySlug = normalizeCountrySlug(country);
  const citySlug = city ? toSeoUrl(String(city)) : null;
  const filtered = hasFilterQuery(params);

  const pageParsed = parseInt(params.page, 10);
  const pageNum =
    Number.isFinite(pageParsed) && pageParsed > 1
      ? Math.min(pageParsed, MAX_URL_PAGE)
      : null;

  let effectiveRole = roleSlug;
  let effectiveCountry = countrySlug;
  let effectiveCategories = Boolean(categories) && !roleSlug && !countrySlug;

  if (filtered && !effectiveRole) {
    const fromRolesQuery = singleRoleSlugFromQuery(params);
    if (fromRolesQuery) {
      effectiveRole = fromRolesQuery;
      effectiveCategories = false;
    }
  }

  const path = buildCleanJobsPath({
    role: effectiveRole,
    country: effectiveCountry,
    city: citySlug,
    categories: effectiveCategories,
  });

  const base = (baseUrl || '').replace(/\/$/, '');
  const pageQuery = !filtered && pageNum ? `?page=${pageNum}` : '';

  return {
    canonical: `${base}${path}${pageQuery}`,
  };
}
